'use client';

import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';

import { ComponentErrorBoundary } from '@/components';
import { useT } from '@/hooks';

function getCurrentDate(languageCode: string) {
  const now = new Date();
  return now.toLocaleDateString(languageCode === 'es' ? 'es-ES' : 'en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function BreakingBanner() {
  const { t, language } = useT();
  const currentDate = getCurrentDate(language.code);

  return (
    <ComponentErrorBoundary componentName="Breaking Banner">
      <Box
        sx={{
          background: (theme) =>
            theme.palette.mode === 'dark'
              ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.15)} 0%, ${alpha(
                  theme.palette.info.main,
                  0.1
                )} 100%)`
              : `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.8)} 0%, ${alpha(
                  theme.palette.primary.main,
                  0.6
                )} 100%)`,
          borderRadius: 3,
          p: { xs: 3, sm: 4, md: 5 },
          mb: 3,
          position: 'relative',
          overflow: { xs: 'visible', sm: 'hidden' },
          border: '1px solid',
          borderColor: (theme) =>
            alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.2 : 0.15),
        }}
      >
        {/* Connected Up/Down Arrow Icons */}
        <Box
          sx={{
            position: 'absolute',
            right: { xs: 8, sm: 40, md: 60 },
            top: '50%',
            transform: 'translateY(-50%)',
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 0.5, sm: 2 },
            zIndex: 1,
          }}
        >
          {/* Down Arrow Circle */}
          <Box
            sx={{
              width: { xs: 32, sm: 80, md: 96 },
              height: { xs: 32, sm: 80, md: 96 },
              borderRadius: '50%',
              bgcolor: (theme) => theme.palette.primary.main,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: (theme) => `0 4px 16px ${alpha(theme.palette.primary.main, 0.3)}`,
              position: 'relative',
              zIndex: 2,
              flexShrink: 0,
              minWidth: { xs: 32, sm: 80, md: 96 },
              maxWidth: { xs: 32, sm: 80, md: 96 },
            }}
          >
            <TrendingDownIcon sx={{ fontSize: { xs: 16, sm: 40, md: 48 }, color: 'white' }} />
          </Box>

          {/* Up Arrow Circle */}
          <Box
            sx={{
              width: { xs: 32, sm: 80, md: 96 },
              height: { xs: 32, sm: 80, md: 96 },
              borderRadius: '50%',
              bgcolor: '#2563eb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: (theme) => `0 4px 16px ${alpha(theme.palette.primary.main, 0.3)}`,
              position: 'relative',
              zIndex: 2,
              flexShrink: 0,
              minWidth: { xs: 32, sm: 80, md: 96 },
              maxWidth: { xs: 32, sm: 80, md: 96 },
            }}
          >
            <TrendingUpIcon sx={{ fontSize: { xs: 16, sm: 40, md: 48 }, color: 'white' }} />
          </Box>
        </Box>

        {/* Content */}
        <Box
          sx={{ position: 'relative', zIndex: 2, maxWidth: { xs: '100%', sm: '70%', md: '60%' } }}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 1.5, fontSize: { xs: '0.75rem', sm: '0.875rem' }, fontWeight: 500 }}
          >
            {currentDate}
          </Typography>
          <Typography
            variant="h4"
            fontWeight={700}
            sx={{
              mb: 1.5,
              fontSize: { xs: '1.75rem', sm: '2.25rem', md: '2.5rem' },
              color: 'text.primary',
            }}
          >
            {t('breaking.title')}
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, lineHeight: 1.6 }}
          >
            {t('breaking.description')}
          </Typography>
        </Box>
      </Box>
    </ComponentErrorBoundary>
  );
}

export default BreakingBanner;
